import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  PageLayout,
  TableSkeleton,
  CompanyModal,
  SuccessPopup,
  EmptyState,
  emptyCompanyIllustration,
  CompanyList,
} from "@components";
import { HiOutlineTruck } from "react-icons/hi";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import { useWorkspaceData } from "@data";
import { useTranslation } from "react-i18next";
import { ICompany } from "../../types";
import { Company } from "../../models";
import { useLocation, useNavigate } from "react-router-dom";

export const CompaniesPage: FC = function () {
  const [isUpsertModalOpen, openInsertModal] = useState(false);
  const [companyData, setCompanyData] = useState(Company.default());
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const { companies, loading } = useWorkspaceData();
  const { t } = useTranslation(["common", "company"]);
  const location = useLocation();
  const navigate = useNavigate();

  const isQueryLoading = loading.companies;

  // Check if we need to show mandatory company creation modal.
  // Note: new workspaces are now sent through the onboarding wizard
  // (`/onboarding`) before they can reach any page, so this is effectively a
  // fallback for edge cases (e.g. every company later deleted).
  const showMandatoryModal = !isQueryLoading && companies.length === 0;

  const handleCompanyCreated = () => {
    setShowSuccessPopup(true);
  };

  const handleCreateVehicle = () => {
    setShowSuccessPopup(false);
    navigate("/resources/vehicles?new=true");
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  useEffect(() => {
    // Preserve old shared links that opened the former split view.
    const params = new URLSearchParams(location.search);
    const companyParam = params.get("company");
    if (companyParam && companies.length > 0) {
      const decodedName = companyParam.toLowerCase();
      const match = companies.find(
        (company) =>
          company.name.toLowerCase() === decodedName.replace(/-/g, " ") ||
          company.name.toLowerCase().replace(/\s+/g, "-") === decodedName,
      );

      if (match) {
        navigate(`/workspace/companies/${match.id}`, { replace: true });
      }
    }
  }, [companies, location.search, navigate]);

  return (
    <PageLayout>
      {isQueryLoading ? (
        <TableSkeleton />
      ) : companies.length === 0 ? (
        // Render empty state when there are no table rows
        <EmptyState
          icon={emptyCompanyIllustration}
          title={t("company:empty_state.title")}
          description={t("company:empty_state.description")}
          buttonText={t("company:empty_state.button_text")}
          buttonClick={() => {
            setCompanyData(Company.default());
            openInsertModal(true);
          }}
        />
      ) : (
        <CompanyList
          companies={companies}
          isLoading={isQueryLoading}
          onCreate={() => {
            setCompanyData(Company.default());
            openInsertModal(true);
          }}
        />
      )}

      {/* Mandatory Company Creation Modal - shows when no companies exist */}
      <CompanyModal
        data={companyData}
        isShowing={showMandatoryModal}
        onConfirm={async (formData: ICompany) => {
          await Company.create(activeWorkspace?.workspaceId ?? "", formData);
          updateCacheKey();
          handleCompanyCreated();
          // Modal will close automatically when companies.length > 0
        }}
        onCancel={() => {}} // Empty function - makes modal non-dismissible
      />

      {/* Existing companies are edited on their dedicated page. */}
      <CompanyModal
        data={companyData}
        isShowing={isUpsertModalOpen}
        onConfirm={async (formData: ICompany) => {
          await Company.create(activeWorkspace?.workspaceId ?? "", formData);
          handleCompanyCreated();
          openInsertModal(false);
          updateCacheKey();
        }}
        onCancel={() => openInsertModal(false)}
      />

      <SuccessPopup
        isOpen={showSuccessPopup}
        title={t("company:success.title")}
        message={t("company:success.message")}
        primaryAction={{
          text: t("company:success.create_vehicle"),
          onClick: handleCreateVehicle,
          variant: "success",
          icon: <HiOutlineTruck className="mr-2 h-5 w-5" />,
        }}
        secondaryAction={{
          text: t("company:success.not_now"),
          onClick: handleCloseSuccessPopup,
          variant: "secondary",
        }}
        onClose={handleCloseSuccessPopup}
      />
    </PageLayout>
  );
};
